import { yupResolver } from '@hookform/resolvers/yup'
import { filter, find, map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import {
  Button,
  Checkbox,
  Combobox,
  DateTimePicker,
  Input,
  Modal,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import { getTranslation } from '../../../account/services/auth'
import { RewardTicket, Vouchers, VouchersPayload } from '../../../common/models'
import {
  VoucherDiscountType,
  VoucherTypes,
  voucherTypes,
} from '../../../common/models/rewards'
import { TicketCategory, TicketList } from '../../../common/models/ticket'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../core/services/tickets'
import translate from '../../../core/services/translate'
import { IUser, searchUsersByPhone } from '../../../core/services/users'
import {
  createBatchVoucher,
  deleteVouchers,
  downloadVoucherSingle,
  generateCode,
  saveVoucher,
  updateVoucher,
} from '../../../core/services/voucher'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import SelectField from '@/components/select-field'

export interface IVoucherValues {
  name?: string
  code?: string
  exportType?: {
    pdf?: boolean
    png?: boolean
    svg?: boolean
    csv?: boolean
  }
  discountType?: 'PERCENTAGE' | 'AMOUNT'
  discountValue?: number
  type?: VoucherTypes
  contact?: IUser[]
  tickets?: RewardTicket[]
  publishDate?: string
  expirationDate?: string
  createdBy?: string
  status?: 'ACTIVE' | 'INACTIVE'
  voucherLimit?: number | null
  unlimited?: boolean | null
  userLimit?: number | null
  userUnlimited?: boolean | null
  usages?: number
  nrOfVouchers?: number
  dubaiIntegration?: boolean
}

export interface VoucherCreateOneToOneModalProps {
  voucher: Vouchers | undefined
  editMode: boolean
  setEditMode?: (e: boolean) => void
  showCreateModal?: boolean
  setShowCreateModal?: (s: boolean) => void
  onVoucherCreated?: () => void
  dubaiIntegration: boolean
  currency: string
}

interface IIndexTicket {
  [index: number]: TicketList[]
}

interface IIndexUsers {
  [index: number]: {
    users: IUser[]
    isLoading: boolean
  }
}

export const VoucherCreateOneToOneModal = (
  props: VoucherCreateOneToOneModalProps,
): JSX.Element => {
  const {
    voucher,
    editMode,
    setEditMode,
    showCreateModal,
    setShowCreateModal,
    onVoucherCreated,
    dubaiIntegration,
    currency,
  } = props

  const allDiscountTypes = ['PERCENTAGE', 'AMOUNT']
  const { festivals } = useAppSelector((s) => s.app)
  const { editionId, festivalName } = useParams()
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [removeTicket, setRemoveTicket] = useState<number>(0)
  const [active, setActive] = useState<boolean>()
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const language = getTranslation()
  const navigate = useNavigate()

  const [submitting, setSubmitting] = useState<boolean>(false)
  const [usersData, setUsersData] = useState<IIndexUsers>({})
  const [tickets, setTickets] = useState<IIndexTicket>({})

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      const { data } = await getAllCategories({ size: 1000 })
      setTicketCategoriesData(data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [editionId])

  useEffect(() => {
    if (showCreateModal) {
      getData()
    }
  }, [showCreateModal, getData])

  useEffect(() => {
    if (voucher) {
      setValue('name', voucher?.label)
      setValue('code', voucher?.code)
      setValue('discountType', voucher.discountType)
      setValue('discountValue', voucher.amount)
      setValue('type', voucher.type)
      if (voucher.tickets) {
        map(voucher.tickets, (ticket, index) => {
          getCategoryTickets(index, ticket.category.id)
          setValue(`tickets.${index}.ticketType`, ticket.category.id)
          setValue(`tickets.${index}.selectedTicket`, ticket.ticket.id)
          setValue(
            `tickets.${index}.dubaiOverwritePriceTypeCode`,
            ticket.dubaiOverwritePriceTypeCode,
          )
        })
      }
      setTimeout(() => {
        setValue('contact', voucher.contact)
      }, 100)
      setValue('publishDate', formatDateTime(voucher.publishDate))
      setValue('expirationDate', formatDateTime(voucher.expiryDate))
      setValue('createdBy', voucher.createdBy)
      setValue('voucherLimit', voucher.voucherLimit)
      setValue('unlimited', voucher.unlimited)
      setValue('userLimit', voucher.userLimit)
      setValue('userUnlimited', voucher.userUnlimited)
      setValue('usages', voucher.usages)
      setValue('status', voucher.status)
      setActive(voucher.status === 'ACTIVE')
    }

    return () => {
      reset()
      setActive(false)
    }
  }, [voucher, editMode])

  useEffect(() => {
    setValue('dubaiIntegration', dubaiIntegration, {
      shouldValidate: true,
    })
  }, [dubaiIntegration])

  const schemaObject = {
    name: yup.string(),
    code: yup.string().when('type', (type: VoucherTypes[], schema) => {
      if (type[0] === 'BATCH') return schema.optional()
      return schema.required()
    }),
    exportType: yup
      .object()
      .shape({
        pdf: yup.boolean().optional(),
        png: yup.boolean().optional(),
        svg: yup.boolean().optional(),
        csv: yup.boolean().optional(),
      })
      .optional(),
    discountType: yup
      .mixed<VoucherDiscountType>()
      .oneOf(['PERCENTAGE', 'AMOUNT']),
    discountValue: yup.number(),
    type: yup
      .mixed<VoucherTypes>()
      .oneOf(['GENERAL', 'SINGLE_USER', 'MULTI_USERS', 'BATCH'])
      .required(),
    tickets: yup
      .array()
      .of(
        yup.object().shape({
          ticketType: yup.string().required(),
          selectedTicket: yup.string().required(),
          dubaiOverwritePriceTypeCode: yup.string().when('dubaiIntegration', {
            is: (val: boolean) => (val ? true : false),
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable().optional(),
          }),
        }),
      )
      .required(),
    publishDate: yup.string().required(),
    expirationDate: yup.string().required(),
    createdBy: yup.string(),
    contact: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          firstName: yup.string().optional(),
          lastName: yup.string().optional(),
          phone: yup.string().optional(),
          email: yup.string().email().optional(),
        }),
      )
      .optional(),
    voucherLimit: yup.number().when('unlimited', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) =>
        schema
          .nullable()
          .optional()
          .transform((val) => (!isNaN(val) ? val : null)),
      otherwise: (schema) =>
        schema.required().transform((val) => (!isNaN(val) ? val : null)),
    }),
    unlimited: yup.boolean().nullable().optional(),
    userLimit: yup.number().when('userUnlimited', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) =>
        schema
          .nullable()
          .optional()
          .transform((val) => (!isNaN(val) ? val : null)),
      otherwise: (schema) =>
        schema.required().transform((val) => (!isNaN(val) ? val : null)),
    }),
    userUnlimited: yup.boolean().nullable().optional(),
    usages: yup.number(),
    status: yup.mixed<'ACTIVE' | 'INACTIVE'>().oneOf(['ACTIVE', 'INACTIVE']),
    nrOfVouchers: yup.number().optional(),
    dubaiIntegration: yup.boolean().optional(),
  }

  const schema = yup.object().shape(schemaObject)

  const downloadVoucher = async (id: string, type: 'PNG' | 'PDF' | 'SVG') => {
    try {
      const response = await downloadVoucherSingle(id, type)
      const href = URL.createObjectURL(response.data)

      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `${id}.${type}`)
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IVoucherValues>({
    mode: 'onChange',
    resolver: yupResolver<IVoucherValues>(schema),
    defaultValues: {
      tickets: [
        {
          ticketType: '',
          selectedTicket: '',
        },
      ],
      contact: [],
      type: voucherTypes[1].value,
    },
  })

  const handleClose = () => {
    setActive(false)
    setShowCreateModal?.(false)
    setEditMode?.(false)
    reset()
  }

  const submit = async (values: IVoucherValues) => {
    try {
      const payload: VouchersPayload = {
        code: values.code,
        discountType: values.discountType as VoucherDiscountType,
        amount: values.discountValue || 0,
        publishDate: values.publishDate
          ? new Date(values.publishDate).valueOf()
          : null,
        expiryDate: values.expirationDate
          ? new Date(values.expirationDate).valueOf()
          : null,
        tickets: map(values.tickets, (ticket, index) => ({
          festivalEdition: {
            id: editionId as string,
            name: festivalName as string,
          },
          ticket: {
            id: ticket.selectedTicket,
            name: tickets[index]
              ? find(tickets[index], {
                  id: ticket.selectedTicket,
                })?.name || ''
              : '',
          },
          category: {
            id: ticket.ticketType,
            name:
              find(ticketCategoriesData, { id: ticket.ticketType })?.name || '',
          },
          ...(ticket.dubaiOverwritePriceTypeCode
            ? {
                dubaiOverwritePriceTypeCode: ticket.dubaiOverwritePriceTypeCode,
              }
            : {}),
        })),
        userLimit: +(values.userLimit || 0),
        userUnlimited: values.userUnlimited || false,
        voucherLimit: +(values.voucherLimit || 0),
        unlimited: values.unlimited || false,
        festivalId: find(festivals, { id: editionId })?.id as string,
        batchId: null,
        batchLabel: values.name,
        users: map(values.contact, (contact) => ({
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          phone: contact.phone,
          email: contact.email,
        })),
        type: values.type,
        id: voucher?.id,
        label: values.name,
        nrOfVouchers: values.nrOfVouchers ? Number(values.nrOfVouchers) : 0,
        pngEnabled: values.exportType?.png || false,
        svgEnabled: values.exportType?.svg || false,
        pdfEnabled: values.exportType?.pdf || false,
        csvEnabled: values.exportType?.csv || false,
      }
      setSubmitting(true)
      const { data, status } = voucher?.id
        ? await updateVoucher(payload)
        : values.type === 'BATCH'
        ? await createBatchVoucher(payload)
        : await saveVoucher(payload)
      if (status === 200) {
        if (watchType === 'MULTI_USERS' || watchType === 'BATCH') {
          handleClose()
          navigate(`/rewards/vouchers/downloads/${festivalName}/${editionId}`)
          return
        }
        if (watchExportType?.pdf) {
          await downloadVoucher(data.id, 'PDF')
        }
        if (watchExportType?.png) {
          await downloadVoucher(data.id, 'PNG')
        }
        if (watchExportType?.svg) {
          await downloadVoucher(data.id, 'SVG')
        }
        onVoucherCreated?.()
        handleClose()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteVoucher = async () => {
    try {
      const { status } = await deleteVouchers({ ids: [voucher?.id as string] })
      if (status === 204) {
        handleClose()
        onVoucherCreated?.()
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handleGenerateCLicked = async () => {
    if (active) {
      navigator.clipboard.writeText(voucher?.code || '')
    } else {
      const res = await generateCode()
      if (res.status === 200) {
        setValue('code', res.data, {
          shouldValidate: true,
        })
        setActive(true)
      }
    }
  }

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tickets',
  })

  const { fields: contactFields, append: appendContact } = useFieldArray({
    control,
    name: 'contact',
  })

  useEffect(() => {
    if (showCreateModal && contactFields.length === 0) {
      appendContact({
        id: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      })
    }
  }, [showCreateModal])

  const handleSearch = async (
    index: number,
    query: string,
    signal: AbortSignal,
  ) => {
    setUsersData((prev) => ({
      ...prev,
      [index]: {
        users: prev?.[index]?.users || [],
        isLoading: true,
      },
    }))

    try {
      const { data, status } = await searchUsersByPhone(query, signal)
      if (status === 200) {
        setUsersData((prev) => ({
          ...prev,
          [index]: {
            users: data,
            isLoading: false,
          },
        }))
      } else {
        setUsersData((prev) => ({
          ...prev,
          [index]: {
            users: prev?.[index]?.users || [],
            isLoading: false,
          },
        }))
      }
    } catch (e) {
      if (signal.aborted) return
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
      setUsersData((prev) => ({
        ...prev,
        [index]: {
          users: prev?.[index]?.users || [],
          isLoading: false,
        },
      }))
    }
  }

  const watchExportType = watch('exportType')
  const watchType = watch('type')
  const discountType = watch('discountType')
  const unlimited = watch('unlimited')
  const userUnlimited = watch('userUnlimited')
  const publishDateWatch = watch('publishDate')
  const expirationDateWatch = watch('expirationDate')

  const ticketCategory = watch('tickets')

  const getCategoryTickets = async (index: number, category?: string) => {
    try {
      const ticketsCopy = { ...tickets }
      if (category) {
        const { data } = await getTicketsByCategoryId(
          category,
          { size: 1000 },
          false,
        )
        ticketsCopy[index] = data.content
      } else {
        if (ticketCategory && ticketCategory[index]?.ticketType) {
          const { data } = await getTicketsByCategoryId(
            ticketCategory[index]?.ticketType,
            { size: 1000 },
            false,
          )
          ticketsCopy[index] = data.content
        }
      }

      ticketsCopy[index] = ticketsCopy[index].sort((a, b) => {
        if (a.published === b.published) {
          if (a.published) {
            return a.ordinal - b.ordinal
          } else {
            if (a.publishTimestamp === b.publishTimestamp) {
              return a.ordinal - b.ordinal
            } else {
              return a.publishTimestamp < b.publishTimestamp ? -1 : 1
            }
          }
        } else {
          return a.published < b.published ? 1 : -1
        }
      })
      setTickets(ticketsCopy)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const deleteFunc = () => {
    remove(removeTicket)
  }

  if (!showCreateModal) return <></>

  // TODO: apply this to the select field
  const generateClassBasedOnPublish = (ticket: TicketList | undefined) => {
    if (!ticket) return undefined

    if (ticket.published) {
      return 'text-green-500'
    } else {
      if (ticket.publishTimestamp - new Date().valueOf() <= 0) {
        return 'text-red-500'
      } else {
        return 'text-yellow-500'
      }
    }
  }

  const generateSelectClassBasedOnPublish = (
    ticket: RewardTicket | undefined,
    index: number,
  ) => {
    if (!ticket) return undefined
    const t = find(tickets[index], (tic) => tic.id === ticket.selectedTicket)
    if (!t) return undefined

    if (t.published) {
      return 'text-green-500'
    } else {
      if (t.publishTimestamp - new Date().valueOf() <= 0) {
        return 'text-red-500'
      } else {
        return 'text-yellow-500'
      }
    }
  }

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-2xl font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  return (
    <>
      <Modal
        open={showCreateModal || false}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Vouchers"
              subtitle={
                editMode
                  ? active
                    ? 'Edit Voucher'
                    : 'View Voucher'
                  : 'Add New Voucher'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {editMode && (
              <Button
                onClick={handleDelete}
                variant="secondary"
                className="mr-auto bg-accent-3 text-white hover:bg-accent-3/90"
              >
                Delete
              </Button>
            )}
            <Button onClick={handleClose} type="button" variant="secondary">
              {editMode ? 'Close' : 'Cancel'}
            </Button>
            <Button
              form="voucher-form"
              type="submit"
              variant="primary"
              loading={submitting}
            >
              {submitting
                ? 'Submitting ...'
                : watchExportType?.csv ||
                  watchExportType?.pdf ||
                  watchExportType?.png ||
                  watchExportType?.svg
                ? 'Save & Download QR'
                : 'Save'}
            </Button>
          </div>
        }
      >
        <SectionTitle>General Information</SectionTitle>
        <form
          id="voucher-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Voucher Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              disabled={editMode && !active}
              placeholder="Enter Name"
            />
          </FormGroup>

          <FormGroup label="Type" error={errors?.type?.message}>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={voucherTypes.map((type) => ({
                    label: type.name,
                    value: type.value,
                  }))}
                  disabled={editMode && !active}
                  placeholder="Select Type"
                  error={errors?.type?.message}
                />
              )}
            />
          </FormGroup>

          {watchType === 'BATCH' ? (
            <FormGroup
              label="Number of Vouchers to create"
              error={errors?.nrOfVouchers?.message}
            >
              <Input
                isInvalid={!!errors?.nrOfVouchers?.message}
                {...register('nrOfVouchers')}
                type="number"
                disabled={editMode && !active}
                placeholder="Number of Vouchers to create"
              />
            </FormGroup>
          ) : (
            <div className="space-y-3">
              {map(contactFields, (field, index) => {
                return (
                  <div
                    key={field.id}
                    className="flex flex-col gap-3 p-4 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50"
                  >
                    <FormGroup
                      label="Phone Number"
                      error={errors?.contact?.[index]?.phone?.message}
                    >
                      <Controller
                        control={control}
                        name={`contact.${index}.phone`}
                        render={({ field }) => (
                          <Combobox
                            value={field.value}
                            onValueChange={(value) => {
                              const selectedUser = usersData?.[
                                index
                              ]?.users?.find((u) => u.phone === value)
                              if (selectedUser) {
                                setValue(
                                  `contact.${index}.phone`,
                                  selectedUser.phone,
                                )
                                setValue(
                                  `contact.${index}.email`,
                                  selectedUser.email,
                                )
                                setValue(
                                  `contact.${index}.firstName`,
                                  selectedUser.firstName,
                                )
                                setValue(
                                  `contact.${index}.lastName`,
                                  selectedUser.lastName,
                                )
                                setValue(`contact.${index}.id`, selectedUser.id)
                              }
                            }}
                            onSearch={(query, signal) =>
                              handleSearch(index, query, signal)
                            }
                            options={
                              usersData?.[index]?.users
                                ?.filter((user) => user.phone)
                                .map((user) => ({
                                  label: user.phone!,
                                  value: user.phone!,
                                })) || []
                            }
                            isLoading={usersData?.[index]?.isLoading}
                            disabled={editMode && !active}
                            placeholder="Search user..."
                            minSearchLength={3}
                            isInvalid={
                              !!errors?.contact?.[index]?.phone?.message
                            }
                            renderOption={(option) => {
                              const user = usersData?.[index]?.users?.find(
                                (u) => u.phone === option.value,
                              )
                              return (
                                <div>
                                  <span>
                                    {option.label}{' '}
                                    <span className="text-white/50">
                                      ({user?.firstName} {user?.lastName})
                                    </span>
                                  </span>
                                </div>
                              )
                            }}
                          />
                        )}
                      />
                    </FormGroup>
                  </div>
                )
              })}
            </div>
          )}

          {watchType === 'MULTI_USERS' && (
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() =>
                appendContact({
                  id: '',
                  firstName: '',
                  lastName: '',
                  phone: '',
                  email: '',
                })
              }
              type="button"
            >
              <span>Add New</span>
              <i className="bi bi-plus-circle" />
            </Button>
          )}

          {watchType !== 'BATCH' && (
            <FormGroup label="Voucher Code" error={errors?.code?.message}>
              <div className="flex gap-2 w-full">
                <Input
                  isInvalid={!!errors?.code?.message}
                  {...register('code')}
                  type="text"
                  disabled={editMode && !active}
                  placeholder="Enter Voucher Code"
                  className="flex-1"
                />
                <Button
                  variant="secondary"
                  disabled={editMode && !active}
                  onClick={handleGenerateCLicked}
                  type="button"
                  size="sm"
                >
                  {active ? 'Copy' : 'Generate'}
                </Button>
              </div>
            </FormGroup>
          )}

          <div className="flex gap-3">
            <FormGroup
              label="Discount Type"
              error={errors?.discountType?.message}
              wrapperClassName="flex-1"
            >
              <Controller
                name="discountType"
                control={control}
                render={({ field }) => (
                  <SelectField
                    value={field.value}
                    onValueChange={field.onChange}
                    options={allDiscountTypes.map((dis) => ({
                      label: dis,
                      value: dis,
                    }))}
                    placeholder="Select Discount Type"
                    error={errors?.discountType?.message}
                    disabled={editMode && !active}
                    outline
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Discount Amount"
              error={errors?.discountValue?.message}
              wrapperClassName="flex-1"
            >
              <div className="relative w-full">
                <Input
                  isInvalid={!!errors?.discountValue?.message}
                  {...register('discountValue')}
                  type="text"
                  disabled={editMode && !active}
                  placeholder="Select Discount Amount"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-monochrome-1">
                  {discountType === 'AMOUNT' ? currency : '%'}
                </span>
              </div>
            </FormGroup>
          </div>

          <div className="flex gap-3">
            <FormGroup
              label="Publish Date"
              error={errors?.publishDate?.message}
              wrapperClassName="flex-1"
            >
              <DateTimePicker
                hasError={!!errors?.publishDate?.message}
                value={
                  publishDateWatch ? new Date(publishDateWatch) : undefined
                }
                onChange={(date) => {
                  register('publishDate').onChange({
                    target: {
                      value: date ? formatDateTime(date.valueOf()) : '',
                    },
                  })
                  setValue(
                    'publishDate',
                    date ? formatDateTime(date.valueOf()) : '',
                    { shouldValidate: true },
                  )
                }}
                placeholder="Enter Publish Date"
                disabled={editMode && !active}
              />
            </FormGroup>
            <FormGroup
              label="Expiration Date"
              error={errors?.expirationDate?.message}
              wrapperClassName="flex-1"
            >
              <DateTimePicker
                hasError={!!errors?.expirationDate?.message}
                value={
                  expirationDateWatch
                    ? new Date(expirationDateWatch)
                    : undefined
                }
                onChange={(date) => {
                  register('expirationDate').onChange({
                    target: {
                      value: date ? formatDateTime(date.valueOf()) : '',
                    },
                  })
                  setValue(
                    'expirationDate',
                    date ? formatDateTime(date.valueOf()) : '',
                    { shouldValidate: true },
                  )
                }}
                placeholder="Enter Expiration Date"
              />
            </FormGroup>
          </div>

          <div className="my-4 h-px w-full bg-monochrome-120" />
          <SectionTitle>Applied Tickets</SectionTitle>

          <div className="flex flex-col gap-3 w-full">
            {map(fields, (field, index) => {
              return (
                <div
                  key={field.id}
                  className="flex flex-col p-3 gap-3 rounded-3xl border border-monochrome-120 bg-monochrome-5-50"
                >
                  <div className="flex items-end gap-3">
                    <FormGroup
                      label="Applied for Tickets"
                      tooltip={translate(
                        language.value,
                        'rewards.vouchers.appliedForTicketsTooltip',
                      )}
                      wrapperClassName="flex-1"
                      error={errors?.tickets?.[index]?.ticketType?.message}
                    >
                      <Controller
                        name={`tickets.${index}.ticketType`}
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            value={field.value || ''}
                            onValueChange={(value) => {
                              field.onChange(value)
                              getCategoryTickets(index)
                            }}
                            options={ticketCategoriesData.map((tick) => ({
                              label: tick.name || '',
                              value: tick.id || '',
                            }))}
                            placeholder="Select Ticket"
                            error={
                              errors?.tickets?.[index]?.ticketType?.message
                            }
                            disabled={editMode && !active}
                            outline
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Select Ticket"
                      tooltip={translate(
                        language.value,
                        'rewards.vouchers.selectTicketsTooltip',
                      )}
                      wrapperClassName="flex-1"
                      error={errors?.tickets?.[index]?.selectedTicket?.message}
                    >
                      <Controller
                        name={`tickets.${index}.selectedTicket`}
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            value={field.value || ''}
                            onValueChange={(value) => {
                              field.onChange(value)
                            }}
                            options={
                              tickets &&
                              tickets[index] &&
                              tickets[index].map((tick) => ({
                                label: (
                                  <span
                                    className={generateClassBasedOnPublish(
                                      tick,
                                    )}
                                  >
                                    {tick.name} - {tick.price} {currency}
                                  </span>
                                ),
                                value: tick.id || '',
                              }))
                            }
                            placeholder="Select Ticket"
                            error={
                              errors?.tickets?.[index]?.selectedTicket?.message
                            }
                            disabled={editMode && !active}
                            outline
                          />
                        )}
                      />
                    </FormGroup>
                    {fields.length > 0 && (
                      <Button
                        onClick={() => {
                          setDeleteConfirmationModal(true)
                          setRemoveTicket(index)
                        }}
                        disabled={editMode && !active}
                        variant="destructive"
                        type="button"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                  {dubaiIntegration && (
                    <>
                      <div className="h-px w-full bg-monochrome-120" />
                      <div>
                        <h5 className="text-xl font-semibold text-monochrome-1 mb-2">
                          Dubai DET Government API
                        </h5>
                        <FormGroup
                          label="overite_prive_type_code:String"
                          error={
                            errors.tickets?.[index]?.dubaiOverwritePriceTypeCode
                              ?.message
                          }
                        >
                          <Input
                            {...register(
                              `tickets.${index}.dubaiOverwritePriceTypeCode`,
                            )}
                            type="text"
                            isInvalid={
                              !!errors.tickets?.[index]
                                ?.dubaiOverwritePriceTypeCode?.message
                            }
                          />
                        </FormGroup>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>

          {(active || !editMode) && (
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() =>
                append({
                  ticketType: '',
                  selectedTicket: '',
                })
              }
              type="button"
            >
              <span>Add New</span>
              <i className="bi bi-plus-circle" />
            </Button>
          )}

          <div className="my-4 h-px w-full bg-monochrome-120" />
          <SectionTitle>Usage Limits</SectionTitle>

          <FormGroup
            label="General Limit of USES"
            error={errors?.voucherLimit?.message}
          >
            <Input
              isInvalid={!!errors?.voucherLimit?.message}
              {...register('voucherLimit')}
              type="text"
              disabled={unlimited || (editMode && !active)}
              placeholder="General Limit of USES"
            />
          </FormGroup>
          <FormGroup label="Unlimited" direction="row">
            <Controller
              name="unlimited"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={editMode && !active}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Limit per User" error={errors?.userLimit?.message}>
            <Input
              isInvalid={!!errors?.userLimit?.message}
              {...register('userLimit')}
              type="text"
              disabled={userUnlimited || (editMode && !active)}
              placeholder="Limit per User"
            />
          </FormGroup>
          <FormGroup label="Unlimited" direction="row">
            <Controller
              name="userUnlimited"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={editMode && !active}
                />
              )}
            />
          </FormGroup>

          <div className="my-4 h-px w-full bg-monochrome-120" />
          <SectionTitle>Export Options</SectionTitle>

          <FormGroup label="Generate QR Codes as">
            <div className="flex flex-col gap-2">
              <FormGroup label="SVG" direction="row">
                <Controller
                  name="exportType.svg"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={editMode && !active}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup label="PNG" direction="row">
                <Controller
                  name="exportType.png"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={editMode && !active}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup label="PDF" direction="row">
                <Controller
                  name="exportType.pdf"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={editMode && !active}
                    />
                  )}
                />
              </FormGroup>
              {watchType === 'BATCH' && (
                <FormGroup label="CSV" direction="row">
                  <Controller
                    name="exportType.csv"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(Boolean(checked))
                        }
                        disabled={editMode && !active}
                      />
                    )}
                  />
                </FormGroup>
              )}
            </div>
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Ticket'}
        deleteFunc={deleteFunc}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle={'Voucher'}
        deleteFunc={deleteVoucher}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
